import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { addLov, apiError, deleteLov, getOperationalAreas, getProcesses, getSites, saveSite } from "../services/AiService";
import { LovItem, Site } from "../types/ai";
import { ErrorAlert, PageShell } from "../component/aces/Shared";

const emptySite: Partial<Site> = { name: "", code: "", airportIata: "", address: "" };

/** ADMIN: sites and the company's process / operational-area lists used as checklist context. */
const SitesScreen = () => {
  const { t } = useTranslation();
  const [sites, setSites] = useState<Site[]>([]);
  const [form, setForm] = useState<Partial<Site>>(emptySite);
  const [processes, setProcesses] = useState<LovItem[]>([]);
  const [areas, setAreas] = useState<LovItem[]>([]);
  const [newProcess, setNewProcess] = useState("");
  const [newArea, setNewArea] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reload = () => {
    getSites().then(setSites).catch((e) => setError(apiError(e)));
    getProcesses().then(setProcesses).catch(() => setProcesses([]));
    getOperationalAreas().then(setAreas).catch(() => setAreas([]));
  };
  useEffect(reload, []);

  const submit = async () => {
    try {
      await saveSite(form);
      setForm(emptySite);
      reload();
    } catch (e) {
      setError(apiError(e));
    }
  };

  const lovBlock = (title: string, items: LovItem[], value: string, setValue: (v: string) => void, type: "PROCESS" | "OPERATIONAL_AREA") => (
    <div className="aces-card">
      <h5>{title}</h5>
      <ul>
        {items.map((i) => (
          <li key={`${i.source}-${i.value}`}>
            {i.value} {i.source === "ACES" && <span className="aces-chip blue">{t("aces.sites.standardChip")}</span>}
            {i.source === "TENANT" && i.id != null && (
              <button type="button" className="aces-icon-btn" style={{ marginLeft: 8, color: "#b71c1c" }} aria-label={`${t("aces.a11y.delete")} ${i.value}`}
                onClick={() => deleteLov(i.id!).then(reload).catch((e) => setError(apiError(e)))}>
                <i className="ri-delete-bin-line" aria-hidden="true"></i>
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="aces-actions">
        <input className="form-control" style={{ flex: 1 }} value={value} onChange={(e) => setValue(e.target.value)} />
        <button className="aces-btn" disabled={!value.trim()} onClick={() => addLov(type, value.trim()).then(() => { setValue(""); reload(); }).catch((e) => setError(apiError(e)))}>
          {t("aces.common.add")}
        </button>
      </div>
    </div>
  );

  return (
    <PageShell title={t("aces.sites.title")}>
      <ErrorAlert message={error} onClose={() => setError(null)} />
      <div className="aces-card">
        <h5>{form.id ? t("aces.sites.edit") : t("aces.sites.add")}</h5>
        <div className="aces-grid-2">
          <div className="aces-field"><label>{t("aces.sites.name")} *</label><input className="form-control" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="aces-field"><label>{t("aces.sites.code")}</label><input className="form-control" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
          <div className="aces-field"><label>{t("aces.sites.airport")}</label><input className="form-control" maxLength={4} value={form.airportIata || ""} onChange={(e) => setForm({ ...form, airportIata: e.target.value })} /></div>
          <div className="aces-field"><label>{t("aces.sites.address")}</label><input className="form-control" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
        </div>
        <div className="aces-actions">
          <button className="aces-btn" disabled={!form.name?.trim()} onClick={submit}>{t("aces.common.save")}</button>
          {form.id && <button className="aces-btn secondary" onClick={() => setForm(emptySite)}>{t("common.cancel")}</button>}
        </div>
      </div>
      <div className="__defaultTable mb-3">
        <table>
          <thead><tr><th>{t("aces.sites.name")}</th><th>{t("aces.sites.code")}</th><th>{t("aces.sites.airport")}</th><th>{t("aces.sites.active")}</th><th></th></tr></thead>
          <tbody>
            {sites.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td><td>{s.code || "—"}</td><td>{s.airportIata || "—"}</td>
                <td>{s.active ? t("aces.common.yes") : t("aces.common.no")}</td>
                <td className="aces-actions">
                  <button className="aces-btn small secondary" onClick={() => setForm(s)}>{t("aces.finding.edit")}</button>
                  <button className="aces-btn small secondary" onClick={() => saveSite({ id: s.id, active: !s.active }).then(reload).catch((e) => setError(apiError(e)))}>
                    {s.active ? t("aces.sites.deactivate") : t("aces.sites.activate")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="aces-grid-2">
        {lovBlock(t("aces.ctx.process"), processes, newProcess, setNewProcess, "PROCESS")}
        {lovBlock(t("aces.ctx.operationalArea"), areas, newArea, setNewArea, "OPERATIONAL_AREA")}
      </div>
    </PageShell>
  );
};

export default SitesScreen;
